<template lang="pug">
	.ai-music--continue-at
		.ai-music--continue-at--continue-form.flex-col.space(ref="form", :class="{ active: continuePopupVisible }", :style="{ '--heightDelta': waveformVisible ? '140px' : '0px'}")
			template(v-if="continuePopupVisible")
				.font-big.upper {{$tCaption('Continue')}}
				.root.ai-music--continue-at
					.flex-col.space-large
						.flex-col.flex-1
							.flex-row.space.align-center.font-small
								.upper {{$tCaption('Continue at')}}
								b {{$Data.secondsToDisplayValue(this.continueAt)}}
							DxSlider(:value="continueAt", :min="0", :max="sourceItem.Duration", @value-changed="e => $setContinueAt(e.value)")
						.flex-col.flex-1
							.upper.grey {{$tCaption('Style to continue')}}
							DxTextBox(:value.sync="style", stylingMode="filled")
						.flex-col.flex-1
							.upper.grey {{$tCaption('Lyrics to continue')}}
							DxScrollView.lyrics-scroll
								.flex-col.space.lyrics
									.row.flex-row.space-small.wrap(v-for="(row, rIdx) in lyricsRows")
										.word.hoverable.clickable(v-for="(word, wIdx) in row.words", @click="setStart(rIdx, wIdx)", :class="{ inactive: rIdx < startRowIdx || (rIdx == startRowIdx && wIdx < startWordIdx) }") {{word}}
					Loader(v-if="isLoading", text="", :fullscreen="true")
				.bottom-panel.flex-row.space.align-center.justify-end
					DxButton(:text="$tCaption('Cancel', '@actions')", @click="continuePopupVisible = false")
					DxButton(:text="$tCaption('Save', '@actions')", @click="confirmContinue", :disabled="!!isLoading", type="success")
</template>

<script>
import _ from 'lodash';
import Detail from './Detail.vue';
import DxButton from 'devextreme-vue/button';
import DxTextBox from 'devextreme-vue/text-box';
import Popup from 'app-core/Popup.vue';
import Loader from 'app-core/Loader.vue';
import DxSlider from 'devextreme-vue/slider';
import DxScrollView from 'devextreme-vue/scroll-view';

export default {
	translationCategory: 'aiMusic.List',
	components: { 
		DxButton, Popup, Detail, DxTextBox, Loader, DxSlider, DxScrollView
	},
	props: ['waveformVisible', 'isDebug'],
	data() {
		return {
			continuePopupVisible: false,
			continueAt: 0,
			sourceItem: null,
			lyrics: null,
			style: null,
			isLoading: 0,
			startWordIdx: 0,
			startRowIdx: 0
		};
	},
	computed: {
		columns() {
			return [
				{ dataField: 'Metadata.Name', caption: () => this.$tCaption('Name'), cellTemplate: 'item-template' },
			];
		},
		playingMusicIdx() {
			return (this.$refs.listView?.items || []).findIndex(f => f === this.playingMusic);
		},
		isPlaying() {
			return this.$refs.player.isPlaying;
		},
		lyricsRows() {
			return (this.lyrics || '').split('\n').reduce((r, row) => {
				r.push({
					words: (row || '').split(' ')
				});
				return r;
			} , []);
		},
		cuttedLyrics() {
			let startRow = _.cloneDeep(this.lyricsRows[this.startRowIdx] || { words: [] });
			startRow.words = startRow.words.slice(this.startWordIdx, 9999);
			return [
				startRow,
				...this.lyricsRows.slice(this.startRowIdx + 1, 9999)
			].map(f => f.words.join(' ')).join('\n');
		}
	},
	watch: {
		
	},
	mounted() {
		if (__DEBUG__)
			window.aiMusicContinueAt = this;

		this.$setContinueAt = _.debounce((v) => this.continueAt = v, 100, {
			leading: false,
			traling: true
		});

		this.$form = this.$refs.form;
		document.body.appendChild(this.$form);
	},
	destroyed() {
		delete this.$setContinueAt;
		document.body.removeChild(this.$form);
	},
	methods: {
		setStart(rIdx, wIdx) {
			this.startRowIdx = rIdx;
			this.startWordIdx = wIdx;
		},
		confirmContinue() {
			this.continuePopupVisible = false;
			this.isLoading++;
			return this.$Data.postExternalApi('AiMusic/Continue', { Oid: this.sourceItem.Oid, ContinueAt: this.continueAt, Debug: this.isDebug, Style: this.Style, Lyric: this.cuttedLyrics }).finally(() => {
				if (this._isDestroyed) return;
				this.isLoading--;
				this.$emit('reload');
			});
		},
		show(aiMusic, continueAt, opts) {
			this.sourceItem = aiMusic;
			this.continuePopupVisible = false;
			this.continueAt = continueAt || aiMusic?.Duration || 0;
			this.lyrics = null;
			this.style = null;
			this.startRowIdx = 0;
			this.startWordIdx = 0;
			this.$nextTick(async () => {
				this.continuePopupVisible = true;

				if (!aiMusic.Metadata.Lyric) {
					this.isLoading++;
					aiMusic = await this.$Data.getExternalApi('AiMusic/Get', `id=${aiMusic.Oid}`).finally(() => this.isLoading--);
				}

				if (opts?.isEnd) {
					aiMusic.Metadata.Style = '[End]';

					let end = '[Music Fades 0:05] [Song Ends]';
					if (aiMusic.Metadata.Lyric.indexOf(end) === -1) {
						aiMusic.Metadata.Lyric += `\r\n${end}`;
					}
				}

				this.lyrics = aiMusic.Metadata.Lyric;
				this.style = aiMusic.Metadata.Style;
			});
		}
	}
};
</script>
<style lang="scss">
	@import "main/themes/generated/variables.base.scss";
	.ai-music--continue-at {
		.lyrics {
			padding: 4px;
		}
		.row {
			.word {
				position: relative;

				&:hover {
					&:before {
						content: '|';
						position: absolute;
						left: -3px;
						top: 0px;
					}
				}

				&.inactive {
					text-decoration: line-through;
					opacity: 0.5;
				}
			}
		}
	}

	.ai-music--continue-at--continue-form {
		position: fixed;
		top: 0px;
		right: 0px;
		height: calc(100% - var(--heightDelta));
		width: 70vw;
		max-width: 680px;
		background: #363640;
		z-index: 1501;
		transform: translateX(100%);
		transition: 0.3s;
		color: #f1f1f1;
		padding: 20px;
		border-left: 4px solid #2a2a33;
		border-bottom: 1px solid #2a2a33;
		border-top: 1px solid #2a2a33;

		input {
			color: #f1f1f1;
		}

		&.active {
			transform: translateX(0%);
		}

		.lyrics-scroll {
			height: calc(100vh - var(--heightDelta) - 280px);
			min-height: 200px;
		}
	}
</style>
